// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0

/**
 * Turning what someone typed into what a carrier accepts.
 *
 * E.164 is `+`, a country code, then the national number, with nothing else:
 * no spaces, no parentheses, no dashes, no extension. Twilio rejects anything
 * else on `To` with error 21211, which surfaces to a clinician as a call that
 * silently never happened.
 *
 * Scope is deliberately narrow. This validates structure — length, digits,
 * country code — and does not know which area codes exist, whether a number is
 * mobile or landline, or whether it is in service. Claiming any of that would
 * mean shipping a carrier database and keeping it current, and being wrong
 * about it is worse than not asserting it.
 */

/** Why a number could not be put in E.164. */
export type PhoneRefusalReason =
  | 'empty'
  | 'contains-letters'
  | 'has-extension'
  | 'too-short'
  | 'too-long'
  | 'invalid-country-code'
  | 'not-a-number';

export interface PhoneRefusal {
  readonly reason: PhoneRefusalReason;
  /** What a user needs to do, in their words. */
  readonly message: string;
  /** What was given, unchanged, so a UI can show it next to the complaint. */
  readonly input: string;
}

export type PhoneResult =
  | { readonly ok: true; readonly e164: string }
  | { readonly ok: false; readonly refusal: PhoneRefusal };

/**
 * Default country calling code, applied only to a bare national number.
 *
 * A separate argument rather than a constant because a tenant outside the NANP
 * would otherwise have every number silently prefixed with the wrong country.
 */
export const DEFAULT_COUNTRY_CODE = '1';

/** The longest an E.164 number may be, excluding the plus. */
const E164_MAX_DIGITS = 15;
/** Shortest plausible international number. */
const E164_MIN_DIGITS = 8;
/** National significant number length in the North American Numbering Plan. */
const NANP_NATIONAL_DIGITS = 10;

/*
 * `[\s.]*` rather than `\s*\.?\s*`.
 *
 * Two unbounded `\s*` separated by an optional literal backtracks quadratically
 * when the separator run never reaches a digit. Measured on the old pattern:
 * "x" plus 20,000 spaces took 183ms, and 50,000 took 997ms. A phone number
 * arrives from a FHIR ContactPoint.value, which is writable through the API, so
 * the input is not bounded by a form field. One character class is linear:
 * the same two inputs take 0.02ms and 0.06ms.
 *
 * Behaviour is unchanged on every case the tests cover — `ext 204`, `x204`,
 * `extension 204`, `ext. 204`, `ext.204` still match, and bare numbers still
 * do not.
 */
const EXTENSION_PATTERN = /(?:\bext(?:ension)?\b|\bx)[\s.]*\d+/i;

function refuse(reason: PhoneRefusalReason, message: string, input: string): PhoneResult {
  return { ok: false, refusal: { reason, message, input } };
}

/**
 * Normalise a human-entered number to E.164.
 *
 * `countryCode` is applied only when the input carries no country code of its
 * own — a bare ten-digit number in the NANP, or eleven digits beginning with
 * one. An input already starting with `+` is taken at its word, because
 * overriding an explicit country code is how an international patient gets
 * dialled domestically.
 */
export function toE164(input: string | undefined, countryCode: string = DEFAULT_COUNTRY_CODE): PhoneResult {
  const raw = (input ?? '').trim();
  if (!raw) {
    return refuse('empty', 'No phone number is recorded.', raw);
  }

  if (EXTENSION_PATTERN.test(raw)) {
    /*
     * An extension cannot be dialled as part of E.164 — it is post-connect
     * DTMF. Stripping it silently would connect the call to a switchboard and
     * leave it there, which looks like a working call to everyone except the
     * person who needed to reach a ward.
     */
    return refuse(
      'has-extension',
      'This number has an extension, which cannot be dialled directly. Record the direct line.',
      raw
    );
  }

  // Letters are a vanity number or a typo; neither is dialable here.
  if (/[a-z]/i.test(raw)) {
    return refuse('contains-letters', 'A phone number cannot contain letters.', raw);
  }

  const hadPlus = raw.startsWith('+');
  const digits = raw.replace(/\D/g, '');

  if (!digits) {
    return refuse('not-a-number', 'That does not look like a phone number.', raw);
  }

  let national: string;
  if (hadPlus) {
    national = digits;
  } else if (digits.length === NANP_NATIONAL_DIGITS) {
    national = `${countryCode}${digits}`;
  } else if (digits.length === NANP_NATIONAL_DIGITS + 1 && digits.startsWith('1') && countryCode === '1') {
    // 1-555-123-4567 as typed by a US user.
    national = digits;
  } else {
    /*
     * Without a leading plus, the only lengths that can be read unambiguously
     * are a national number and a national number with a trunk prefix. Nine
     * digits is not a short international number, it is a ten-digit number
     * with a typo — and padding it to ten reaches a stranger.
     */
    return refuse(
      digits.length < NANP_NATIONAL_DIGITS ? 'too-short' : 'too-long',
      'That number cannot be read on its own. Record it with a country code, like +1 for the US.',
      raw
    );
  }

  if (national.length < E164_MIN_DIGITS) {
    return refuse('too-short', 'That number is too short to dial.', raw);
  }
  if (national.length > E164_MAX_DIGITS) {
    return refuse('too-long', 'That number is too long to be a phone number.', raw);
  }
  if (national.startsWith('0')) {
    // No country code begins with zero; this is a national trunk prefix that
    // was never stripped.
    return refuse('invalid-country-code', 'That number is missing a country code.', raw);
  }

  return { ok: true, e164: `+${national}` };
}

/** Whether a string is already valid E.164, with no normalisation attempted. */
export function isE164(value: string | undefined): boolean {
  return typeof value === 'string' && /^\+[1-9]\d{7,14}$/.test(value);
}

/**
 * A number formatted for a human to read back over the phone.
 *
 * NANP numbers get the familiar grouping; everything else is returned as
 * entered rather than guessed at, because grouping rules differ by country and
 * a wrongly grouped number is harder to read aloud than an ungrouped one.
 */
export function formatForDisplay(e164: string): string {
  const match = /^\+1(\d{3})(\d{3})(\d{4})$/.exec(e164);
  return match ? `(${match[1]}) ${match[2]}-${match[3]}` : e164;
}
